/**
 * AI Engine — persistance mémoire long-terme.
 *
 * Phase 1 : backend `localStorage.nestor_ai` (port direct du proto).
 * Phase 2.4 : backend **IndexedDB** (store `ai-memory`), avec :
 *
 *   - un **cache mémoire** maintenu en RAM pour conserver une API
 *     synchrone (`getMemory()` retourne immédiatement) — l'AI Engine
 *     est lue à chaque suggestion, async serait disproportionné ;
 *   - une **hydratation au boot** : à l'import du module on charge
 *     IDB → cache (fire-and-forget). Avant que l'hydratation ne se
 *     termine, `getMemory()` retourne un objet vide — l'AI Engine
 *     se contente de suggérer moins (pas de patterns connus encore) ;
 *   - une **migration douce** : si IDB est vide et que `localStorage
 *     .nestor_ai` existe (cas de l'utilisateur Phase 1), on copie son
 *     contenu dans IDB puis on supprime l'entrée localStorage. Cette
 *     migration est idempotente et s'exécute une seule fois ;
 *   - une **persistance fire-and-forget** : `saveMemory()` met à jour
 *     le cache puis déclenche un `putAiMemory()` IDB sans attendre.
 *
 * L'API publique (`getMemory`, `saveMemory`, `acceptSuggestion`,
 * `dismissSuggestion`) reste **strictement identique** à celle de
 * la Phase 1 — zéro propagation aux call sites (`logic.ts`,
 * `index.ts`, `coparenting/HomeView.ts`).
 */
import { deleteAiMemory, getAiMemoryEntry, putAiMemory } from '../../storage/aiMemory.js';
import type { AiMemory } from './types.js';

/**
 * Clé localStorage Phase 1 — conservée pour la migration douce.
 * Après migration réussie, l'entrée est supprimée et ne réapparaît
 * jamais (les écritures suivantes vont en IDB).
 */
const LEGACY_STORAGE_KEY = 'nestor_ai';

const EMPTY_MEMORY: AiMemory = {
  missedItems: {},
  latePrep: 0,
  accepted: [],
  ignored: [],
  patterns: {},
};

/** Cache mémoire — source de vérité synchrone, hydraté depuis IDB au boot. */
let cachedMemory: AiMemory = cloneEmpty();

/**
 * Promise de l'hydratation initiale. Conservée pour :
 *   - dédupliquer les appels concurrents à `ensureInitialized()` ;
 *   - permettre aux tests d'attendre la fin de l'hydratation avant
 *     les assertions (`_awaitInitForTests()`).
 */
let initPromise: Promise<void> | null = null;

/**
 * Lance (ou récupère) l'hydratation IDB. Idempotent.
 */
function ensureInitialized(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = hydrate();
  return initPromise;
}

/**
 * Logique d'hydratation : IDB → cache. Tombe sur la migration
 * localStorage si IDB est vide.
 */
async function hydrate(): Promise<void> {
  try {
    const entry = await getAiMemoryEntry();
    if (entry) {
      cachedMemory = normalize(entry.data);
      return;
    }
    // IDB vide — tentative de migration depuis localStorage.
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as unknown;
      cachedMemory = normalize(parsed);
      // Pousse en IDB AVANT de retirer de localStorage — si l'écriture
      // IDB rate (Safari privacy mode), on garde la donnée localStorage
      // comme fallback.
      const persisted = await putAiMemory(cachedMemory);
      if (persisted) {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      }
    } catch {
      // JSON corrompu — on laisse localStorage tel quel pour ne pas
      // perdre une trace de l'état (au cas où l'utilisateur trouve
      // un moyen de réparer). Cache reste à empty.
    }
  } catch (err) {
    console.warn('[ai-engine:memory] hydrate failed:', err);
  }
}

// Démarre l'hydratation dès l'import du module — fire-and-forget. Les
// premiers `getMemory()` retournent un cache vide ; ça remplit ~10 ms
// plus tard. L'AI Engine tolère un état initial vide (pas de patterns
// connus = moins de suggestions, pas de crash).
void ensureInitialized();

/**
 * Normalise un payload brut (de localStorage ou IDB) en `AiMemory`
 * complet. Tolérant aux champs manquants — fidèle au comportement
 * de la Phase 1 / du proto.
 */
function normalize(raw: unknown): AiMemory {
  if (!raw || typeof raw !== 'object') return cloneEmpty();
  const obj = raw as Partial<AiMemory>;
  return {
    missedItems: obj.missedItems ?? {},
    latePrep: obj.latePrep ?? 0,
    accepted: obj.accepted ?? [],
    ignored: obj.ignored ?? [],
    patterns: obj.patterns ?? {},
  };
}

/**
 * Lit la mémoire depuis le cache RAM. Retourne une **copie défensive**
 * pour que les call sites puissent muter localement sans corrompre le
 * cache (Phase 1 avait le même contrat — fidèle).
 *
 * Si l'hydratation n'est pas encore terminée, retourne un objet vide.
 * L'AI Engine s'en accommode (suggestions = ensemble vide → l'UI
 * affiche moins de tips, pas d'erreur).
 */
export function getMemory(): AiMemory {
  return cloneMemory(cachedMemory);
}

/**
 * Persiste la mémoire. Met à jour le cache **immédiatement** (sync)
 * puis déclenche un put IDB **en arrière-plan**. Si l'écriture IDB
 * échoue (quota, privacy mode), le cache reste cohérent pour la
 * session courante — comportement équivalent au proto.
 *
 * Sérialise les call sites concurrents : la dernière écriture gagne.
 * Pas de transaction au sens BDD (l'AI memory n'a pas de contrainte
 * de cohérence multi-champ — c'est un objet bag).
 */
export function saveMemory(mem: AiMemory): void {
  cachedMemory = cloneMemory(mem);
  void persistAsync(cachedMemory);
}

async function persistAsync(mem: AiMemory): Promise<void> {
  // Attend que l'hydratation initiale ait fini avant d'écrire — sinon
  // une saveMemory très précoce pourrait être écrasée par l'hydratation
  // qui suit (race condition).
  await ensureInitialized();
  await putAiMemory(mem);
}

/**
 * Marque une suggestion comme acceptée. Idempotent. Retire l'id de la
 * liste des suggestions ignorées si présent.
 */
export function acceptSuggestion(id: string): void {
  const mem = getMemory();
  if (!mem.accepted.includes(id)) mem.accepted.push(id);
  mem.ignored = mem.ignored.filter((i) => i !== id);
  saveMemory(mem);
}

/**
 * Marque une suggestion comme écartée. Idempotent. La suggestion sera
 * skipée par `generateSuggestions()` lors des prochains appels.
 */
export function dismissSuggestion(id: string): string {
  const mem = getMemory();
  if (!mem.ignored.includes(id)) mem.ignored.push(id);
  saveMemory(mem);
  return id;
}

/**
 * Réinitialise complètement la mémoire (utilitaire de tests, pas exposé
 * dans l'index public).
 *
 * Phase 2.4 : purge cache + IDB + localStorage legacy + reset
 * `initPromise` pour que le prochain test parte sur une hydratation
 * fraîche.
 */
export async function _resetMemoryForTests(): Promise<void> {
  cachedMemory = cloneEmpty();
  initPromise = null;
  try {
    await deleteAiMemory();
  } catch {
    // ignore — IDB peut-être indispo
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }
}

/**
 * @internal pour tests — attend que l'hydratation initiale se
 * termine. À appeler en `beforeEach` quand le test seed quelque chose
 * dans IDB ou localStorage AVANT d'observer le cache.
 */
export async function _awaitInitForTests(): Promise<void> {
  await ensureInitialized();
}

function cloneEmpty(): AiMemory {
  return cloneMemory(EMPTY_MEMORY);
}

function cloneMemory(mem: AiMemory): AiMemory {
  return {
    missedItems: { ...mem.missedItems },
    latePrep: mem.latePrep,
    accepted: [...mem.accepted],
    ignored: [...mem.ignored],
    patterns: { ...mem.patterns },
  };
}
